import React, { useMemo, useState } from "react";
import { DynamicIcon } from "lucide-react/dynamic";
import { Input } from "@/components/ui/input";
import {
  LUCIDE_STAMP_ICON_IDS,
  resolveStampIconId,
} from "@/lib/lucideStampIconIds";

const PAGE_SIZE = 96;

/**
 * Searchable Lucide (rounded line) icon picker for wallet stamp circles.
 */
export default function StampIconPicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const selected = resolveStampIconId(value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LUCIDE_STAMP_ICON_IDS;
    return LUCIDE_STAMP_ICON_IDS.filter((id) => id.includes(q));
  }, [query]);

  const visible = filtered.slice(0, limit);

  return (
    <div className="space-y-2" data-testid="stamp-icon-picker">
      <div className="flex items-center gap-2">
        <div
          className="w-9 h-9 rounded-full border-2 border-purple-500 bg-purple-50 flex items-center justify-center shrink-0"
          title={selected}
        >
          <DynamicIcon name={selected} className="w-4 h-4 text-purple-500" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-ink/50">Icon (Lucide line)</p>
          <p className="text-[11px] font-medium text-ink truncate" data-testid="stamp-icon-selected">
            {selected}
          </p>
        </div>
      </div>

      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setLimit(PAGE_SIZE);
        }}
        placeholder="Search icons…"
        className="h-7 text-xs"
        data-testid="stamp-icon-search"
      />

      <div className="grid grid-cols-6 gap-1 max-h-52 overflow-y-auto pr-0.5">
        {visible.map((id) => {
          const active = id === selected;
          return (
            <button
              key={id}
              type="button"
              title={id}
              onClick={() => onChange(id)}
              className={`aspect-square rounded border-2 flex items-center justify-center ${
                active
                  ? "border-purple-500 bg-purple-50 text-purple-500"
                  : "border-ink/10 text-ink/60 hover:border-ink/30"
              }`}
              data-testid={`stamp-icon-${id}`}
            >
              <DynamicIcon name={id} className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between text-[10px] text-ink/40">
        <span>
          {filtered.length} icon{filtered.length === 1 ? "" : "s"}
          {visible.length < filtered.length ? ` · showing ${visible.length}` : ""}
        </span>
        {visible.length < filtered.length && (
          <button
            type="button"
            className="text-purple-500 font-semibold hover:underline"
            onClick={() => setLimit((n) => n + PAGE_SIZE)}
            data-testid="stamp-icon-show-more"
          >
            Show more
          </button>
        )}
      </div>
    </div>
  );
}
